import classNames from "classnames";
import { t } from "i18next";
import React, { ReactNode, useEffect, useState } from "react";
import { Postalcode, Location } from "..";
import { item } from "../Location/Location";
import { fetchLocations } from "../../../api/location";
import Styles from "./LocationWrapper.module.scss";
import { Locations } from "@/types/types";

export enum Directions {
  horizontal = "horizontal",
  vertical = "vertical",
}

export enum Types {
  gas = "gas",
  electricity = "electricity",
  heatPump = "heatPump",
}

interface props {
  postalcode: string;
  location: string;
  onChangePostalcode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFetchLocations: (locationsResponse: LocationsResponse) => void;
  onChangeLocation: (val: string) => void;
  onBlurPostalcode?: () => void;
  onBlurLocation?: () => void;
  onFocusPostalcode?: () => void;
  onFocusLocation?: () => void;
  error?: string;
  className?: string;
  type: Types;
  title?: string;
  postalcodeTitle?: string | ReactNode;
  locationTitle?: string | ReactNode;
  direction?: Directions;
  required?: boolean;
  onErrorPostalcode?: (error: string | undefined) => void;
  onErrorLocation?: (error: string | undefined) => void;
  runValidation?: boolean;
  postalcodeValidation?: ((value: unknown) => string | undefined) | Array<(value: unknown) => string | undefined>;
  locationValidation?: ((value: unknown) => string | undefined) | Array<(value: unknown) => string | undefined>;
  autoFocus?: boolean;
  postalcodeClass?: string;
  locationClass?: string;
  newStyle?: boolean;
}

const mapLocations = (locations: Locations) => {
  return locations.map((location) => {
    return { text: location.name, value: location.id?.toString() };
  });
};

const LocationWrapper = ({
  className,
  postalcode,
  location,
  onChangePostalcode,
  onFetchLocations,
  onChangeLocation,
  onErrorPostalcode,
  onErrorLocation,
  onBlurPostalcode,
  onBlurLocation,
  onFocusPostalcode,
  onFocusLocation,
  type,
  title,
  postalcodeTitle,
  locationTitle,
  direction = Directions.horizontal,
  required,
  runValidation,
  postalcodeValidation,
  locationValidation,
  autoFocus,
  postalcodeClass,
  locationClass,
  newStyle,
}: props) => {
  const [locations, setLocations] = useState<item[]>([]);
  const [postalcodeError, setPostalcodeError] = useState<string | undefined>("");
  const [loading, setLoading] = useState(false);

  // fetches new locations when a postalcode with a length of 5 is given
  useEffect(() => {
    const getLocations = async () => {
      setLoading(true);
      fetchLocations(postalcode, type).then((res) => {
        setLoading(false);
        onFetchLocations(res);
        if (res.locations !== null) {
          const locations = mapLocations(res.locations);
          setLocations(locations);
        } else {
          setPostalcodeError(t("validations.invalidPostalcode") || undefined);
        }
      });
    };
    if (postalcode.length === 5) {
      getLocations();
    }
    //eslint-disable-next-line
  }, [postalcode, type]);

  // Reset locations when postalcode is changed
  useEffect(() => {
    if (postalcode.length < 5) {
      if (!!location) {
        onChangeLocation("");
      }
      if (locations.length > 0) {
        setLocations([]);
      }
    }
  }, [location, postalcode, setLocations, onChangeLocation, locations.length]);

  const handlePostalcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPostalcodeError("");
    onChangePostalcode(e);
  };

  // new design generic handeling
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (!target) return;

    const parentDiv = target.closest('[class^="Input-module_formField_"]');
    if (parentDiv) {
      const spanElement = parentDiv.querySelector('[class^="Input-module_title_"]');
      if (spanElement) {
        spanElement?.classList?.add("float-above", "floatAbove", "in-focus");
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const target = event.target as HTMLInputElement;
      if (!target) return;
      const classNames = ["in-focus"];
      if (target.value === "") {
        classNames.push("float-above");
        classNames.push("floatAbove");
      }
      const parentDiv = target.closest('[class^="Input-module_formField_"]');

      if (parentDiv) {
        const spanElement = parentDiv.querySelector('[class^="Input-module_title_"]');
        if (spanElement) {
          spanElement?.classList?.remove(...classNames);
        }
      }
    }, 200);
  };

  return (
    <div
      className={classNames(
        Styles.wrapper,
        className,
        direction === Directions.vertical ? Styles.vertical : Styles.horizontal
      )}
    >
      {title && <span className={Styles.title}>{title}</span>}
      <Postalcode
        className={classNames(Styles.postalcodeInput, postalcodeClass, {
          [Styles.horizontal]: direction === Directions.horizontal,
        })}
        titleClass={newStyle && postalcode.length ? "floatAbove" : ""}
        value={postalcode}
        onChange={handlePostalcodeChange}
        error={postalcodeError}
        required={required}
        title={postalcodeTitle}
        onValidate={onErrorPostalcode}
        runValidation={runValidation}
        customValidation={postalcodeValidation}
        autoFocus={autoFocus}
        onBlur={(e) => {
          handleBlur(e);
          onBlurPostalcode && onBlurPostalcode();
        }}
        onFocus={(e) => {
          handleFocus(e);
          onFocusPostalcode && onFocusPostalcode();
        }}
        placeholder={newStyle ? " " : undefined}
      />
      <Location
        className={classNames(Styles.locationInput, locationClass, {
          [Styles.vertical]: direction === Directions.vertical,
        })}
        value={location}
        options={locations}
        onChange={onChangeLocation}
        title={locationTitle}
        required={required}
        onValidate={onErrorLocation}
        runValidation={runValidation}
        loading={loading}
        customValidation={locationValidation}
        onBlur={onBlurLocation}
        onFocus={onFocusLocation}
        placeholder={newStyle ? " " : undefined}
      />
    </div>
  );
};

export default LocationWrapper;
